import { useEffect, useRef, useState } from 'react';

const milestones = [
  { year: '2020', title: '天天漫画诞生', desc: '天天漫画正式上线，首批收录1000部正版漫画资源。' },
  { year: '2021', title: '用户突破100万', desc: '天天漫画获得首批用户认可，下载量突破100万。' },
  { year: '2022', title: 'AI推荐上线', desc: '推出AI智能推荐引擎，为用户精准推送个性化内容。' },
  { year: '2023', title: '多端同步发布', desc: '支持iOS、Android、HarmonyOS和Windows四大平台。' },
  { year: '2024', title: '漫画突破5万部', desc: '漫画资源突破5万部，覆盖全品类漫画需求。' },
  { year: '2025', title: '用户突破3000万', desc: '天天漫画累计用户突破3000万，日活超过500万。' },
  { year: '2026', title: '全新5.0版本', desc: '天天漫画发布全新5.0版本，界面全面升级，体验再进化。' },
];

const team = [
  { name: '张小明', role: '创始人 & CEO', desc: '资深漫画爱好者，曾任职于知名互联网公司产品总监。' },
  { name: '李思雨', role: '产品总监', desc: '10年产品经验，主导天天漫画全部产品设计与迭代。' },
  { name: '王大力', role: '技术负责人', desc: '前大厂架构师，负责天天漫画APP的技术架构与性能优化。' },
  { name: '陈漫画', role: '内容运营总监', desc: '资深漫评人，负责天天漫画正版内容引进与社区运营。' },
];

function ScrollReveal({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.unobserve(el); } },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}>
      {children}
    </div>
  );
}

export default function About() {
  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="px-4 md:px-8 lg:px-16 xl:px-24 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-3xl overflow-hidden bg-pink-500">
            <img
              src="/images/logo/logo.webp"
              alt="天天漫画"
              className="w-full h-full object-cover"
            />
          </div>
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            关于天天漫画
          </h1>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto">
            天天漫画致力于打造中国最优质的漫画阅读平台，让每个人都能随时随地享受漫画阅读的乐趣。
          </p>
        </div>
      </section>

      <div className="px-4 md:px-8 lg:px-16 xl:px-24 pb-20 md:pb-28 max-w-6xl mx-auto">
        {/* Mission */}
        <ScrollReveal>
          <div className="border border-gray-100 p-8 md:p-12 mb-16">
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="font-serif text-2xl md:text-3xl font-bold text-gray-900 mb-4">我们的使命</h2>
                <p className="text-gray-500 leading-relaxed mb-4">
                  天天漫画坚信，优质的内容应该触手可及。我们与国内外数百家漫画出版社和内容创作者合作，为用户提供、高清、丰富的漫画阅读体验。
                </p>
                <p className="text-gray-500 leading-relaxed">
                  同时，天天漫画致力于为原创作者提供更好的创作环境和收益保障，推动中国漫画产业的健康发展。让每一位漫画爱好者都能在这里找到属于自己的故事。
                </p>
              </div>
              <div className="relative">
                <img
                  src="/images/about/about-mission.webp"
                  alt="天天漫画吉祥物形象"
                  className="w-full aspect-[3/2] object-cover"
                />
              </div>
            </div>
          </div>
        </ScrollReveal>

        {/* Timeline */}
        <ScrollReveal>
          <div className="mb-16">
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-gray-900 mb-10 text-center">发展历程</h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-pink-200 md:-translate-x-1/2" />
              <div className="space-y-10">
                {milestones.map((m, i) => (
                  <div key={m.year} className={`relative flex items-start md:items-center gap-6 md:gap-0 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className="md:w-1/2 md:px-12">
                      <div className={`bg-white border border-gray-100 p-6 ${i % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                        <span className="font-serif text-lg font-bold text-pink-500 mb-2 block">{m.year}</span>
                        <h3 className="font-bold text-gray-900 mb-1">{m.title}</h3>
                        <p className="text-sm text-gray-500">{m.desc}</p>
                      </div>
                    </div>
                    <div className="absolute left-4 md:left-1/2 w-4 h-4 rounded-full bg-pink-500 border-4 border-white shadow-sm md:-translate-x-1/2 flex-shrink-0 mt-6 md:mt-0" />
                    <div className="md:w-1/2" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </ScrollReveal>

        {/* Team */}
        <ScrollReveal>
          <div className="mb-16">
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-gray-900 mb-10 text-center">核心团队</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {team.map((member) => (
                <div key={member.name} className="border border-gray-100 p-6 text-center">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-pink-500 flex items-center justify-center text-white text-2xl font-bold font-serif">
                    {member.name[0]}
                  </div>
                  <h3 className="font-bold text-gray-900 mb-1">{member.name}</h3>
                  <p className="text-pink-500 text-sm font-medium mb-3">{member.role}</p>
                  <p className="text-sm text-gray-500">{member.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </ScrollReveal>
      </div>
    </div>
  );
}